import { TemplateRevisionWarningStatuses, UpdateCheckStatus } from '../../types';
import { AdditionalResource, ContentEditStatus } from '../../store/context/types';
import {
    createPciStimulusTemplatesActionCreators,
    FetchPciStimulusTemplatesAction,
    FetchPciStimulusTemplatesSuccessAction,
    FetchPciStimulusTemplatesErrorAction,
} from '../../services/pciStimulusTemplatesActionsFactory';
import { ContentStylePreset, PreviousSelectedDAKData, SymbolPreset } from './reducer';

export enum TypeKeys {
    ITEM_SAVE_REQUEST = 'contentEditor/ITEM_SAVE_REQUEST',
    REQUEST_QUESTION_RELOAD = 'contentEditor/REQUEST_QUESTION_RELOAD',
    ITEM_SAVE_IN_PROGRESS = 'contentEditor/ITEM_SAVE_IN_PROGRESS',
    ITEM_SAVE_RESPONSE = 'contentEditor/ITEM_SAVE_RESPONSE',
    STORE_EDITOR_IFRAME_REFERENCE = 'contentEditor/STORE_EDITOR_IFRAME_REFERENCE',
    EDITOR_HAS_LOADED = 'contentEditor/EDITOR_HAS_LOADED',
    RESET_EDITOR_LOADED = 'contentEditor/RESET_EDITOR_LOADED',
    PREVIEW_IN_NEW_WINDOW = 'contentEditor/PREVIEW_IN_NEW_WINDOW',
    PREVIEW_TAB_CHANGE = 'contentEditor/PREVIEW_TAB_CHANGE',
    SET_TEMPLATE_DECLARATIONS = 'contentEditor/SET_QUESTION_DATA',
    SET_AUDIO_SCRIPT_TEXT = 'contentEditor/SET_AUDIO_SCRIPT_TEXT',
    SET_AUDIO_SCRIPT_RESOURCE = 'contentEditor/SET_AUDIO_SCRIPT_RESOURCE',
    SET_ASSESSMENT_ITEM_XML = 'contentEditor/SET_ASSESSMENT_ITEM_XML',
    SET_CONTENT_EDIT_STATUS = 'contentEditor/SET_CONTENT_EDIT_STATUS',
    RESET_CONTENT_EDIT_STATUS = 'contentEditor/RESET_CONTENT_EDIT_STATUS',
    UPDATE_CONTENT_IN_CONTEXT_TO_LIVE_REVISION = 'contentEditor/UPDATE_CONTENT_IN_CONTEXT_TO_LIVE_REVISION',
    UPDATE_TEMPLATE_AND_CONTENT_IN_CONTEXT = 'contentEditor/UPDATE_TEMPLATE_AND_CONTENT_IN_CONTEXT',
    DUPLICATE_AND_REPLACE_CONTENT_IN_CONTEXT = 'contentEditor/DUPLICATE_AND_REPLACE_CONTENT_IN_CONTEXT',
    KEEP_CURRENT_CHANGES_FOR_TEMPLATE = 'contentEditor/KEEP_CURRENT_CHANGES_FOR_TEMPLATE',
    FETCH_AUDIO_SCRIPT_DEPENDENCIES = 'context/FETCH_CURRENT_RESOURCE',
    SAVE_AUDIO_SCRIPT_RESOURCE = 'structureEditor/SAVE_AUDIO_SCRIPT_RESOURCE',
    CONTENT_RENAME = 'contentEditor/CONTENT_RENAME',
    SAVE_DYNAMIC_ANSWER_KEY = 'contentEditor/SAVE_DYNAMIC_ANSWER_KEY',
    SET_CONTENT_STYLE_PRESET = 'contentEditor/SET_CONTENT_STYLE_PRESET',
    SET_CONTENT_STYLE_PRESETS = 'contentEditor/SET_CONTENT_STYLE_PRESETS',
    SET_SYMBOL_PALETTE__PRESETS = 'contentEditor/SET_SYMBOL_PALETTE__PRESETS',
    OPEN_QUESTION_TEST_CASES = 'contentEditor/OPEN_QUESTION_TEST_CASES',
    RESET_AUDIO_SCRIPT_DATA = 'contentEditor/RESET_AUDIO_SCRIPT_DATA',
    SET_PREVIOUS_SELECTED_DAK_CONFIG = 'dynamicAnswerKeys/SET_PREVIOUS_SELECTED_DAK_CONFIG',
    HAS_XML_ERROR = 'contentEditor/XML_ERROR_FLAG',
    ADD_OR_UPDATE_URL_RESOURCE = 'structureEditor/ADD_OR_UPDATE_URL_RESOURCE',
    FETCH_PCI_STIMULUS_TEMPLATES = 'contentEditor/FETCH_PCI_STIMULUS_TEMPLATES',
    FETCH_PCI_STIMULUS_TEMPLATES_SUCCESS = 'contentEditor/FETCH_PCI_STIMULUS_TEMPLATES_SUCCESS',
    FETCH_PCI_STIMULUS_TEMPLATES_ERROR = 'contentEditor/FETCH_PCI_STIMULUS_TEMPLATES_ERROR',
}

export type EditorLoaded = {
    featureId: string;
    type: TypeKeys.EDITOR_HAS_LOADED;
}

export type ResetEditorLoaded = {
    featureId: string;
    type: TypeKeys.RESET_EDITOR_LOADED;
}

export type ItemSaveRequest = {
    featureId: string;
    isTemplate?: boolean;
    type: TypeKeys.ITEM_SAVE_REQUEST;
    isDynamicAnswerKey?: boolean;
    isPrintView?: boolean;
}

export type ItemSaveInProgress = {
    featureId: string;
    inProgress: boolean;
    type: TypeKeys.ITEM_SAVE_IN_PROGRESS;
}

export type RenameContent = {
    type: TypeKeys.CONTENT_RENAME;
    featureId: string;
    title: any;
    contentItemId: number;
    contextContentItemId?: number;
}

export type SetTemplateDeclarations = {
    featureId: string;
    data: any;
    type: TypeKeys.SET_TEMPLATE_DECLARATIONS;
}

export type SetAudioScriptText = {
    featureId: string;
    data: any;
    type: TypeKeys.SET_AUDIO_SCRIPT_TEXT;
}

export type SetAudioScriptResource = {
    featureId: string;
    data: AdditionalResource;
    type: TypeKeys.SET_AUDIO_SCRIPT_RESOURCE;
}

export type SetAssessmentItemXML = {
    featureId: string;
    data: any;
    type: TypeKeys.SET_ASSESSMENT_ITEM_XML;
}

export type ItemSaveResponse = {
    featureId: string;
    contentItemId: number;
    payload: any;
    type: TypeKeys.ITEM_SAVE_RESPONSE;
}

export type StoreContentEditorIframeReference = {
    featureId: string;
    iframeTarget: HTMLIFrameElement;
    type: TypeKeys.STORE_EDITOR_IFRAME_REFERENCE;
}

export type RequestQuestionReload = {
    featureId: string;
    type: TypeKeys.REQUEST_QUESTION_RELOAD;
}

export type PreviewInNewWindowAction = {
    featureId: string;
    previewLayoutMode: string;
    type: TypeKeys.PREVIEW_IN_NEW_WINDOW;
}

export type PreviewTabChangeAction = {
    featureId: string;
    activePreviewTabIndex: number;
    type: TypeKeys.PREVIEW_TAB_CHANGE;
};

export type SetContentEditStatusAction = {
    featureId: string;
    contentEditStatus: ContentEditStatus;
    type: TypeKeys.SET_CONTENT_EDIT_STATUS;
};

export type UpdateContentInContextToLiveRevisionAction = {
    featureId: string;
    contentItemId: number;
    type: TypeKeys.UPDATE_CONTENT_IN_CONTEXT_TO_LIVE_REVISION;
    onSuccessCallback?: () => void;
};

export type UpdateTemplateAndContentInContextAction = {
    featureId: string;
    contentItemId: number;
    checkedOptions: UpdateCheckStatus;
    templateContentItemId: number;
    type: TypeKeys.UPDATE_TEMPLATE_AND_CONTENT_IN_CONTEXT;
};

export const renameContent = (featureId: string, title: any, contentItemId: number,
    contextContentItemId?: number): RenameContent => ({
    featureId,
    title,
    contentItemId,
    contextContentItemId,
    type: TypeKeys.CONTENT_RENAME,
});

export type DuplicateAndReplaceContentInContext = {
    featureId: string;
    contentItemId: number;
    type: TypeKeys.DUPLICATE_AND_REPLACE_CONTENT_IN_CONTEXT;
};

export type SetLoadTemplateRevisionAsItIs = {
    featureId: string;
    contentItemId: number;
    loadTemplateRevisionAsItIs: TemplateRevisionWarningStatuses;
    type: TypeKeys.KEEP_CURRENT_CHANGES_FOR_TEMPLATE;
};

export type ResetContentEditStatusAction = {
    featureId: string;
    type: TypeKeys.RESET_CONTENT_EDIT_STATUS;
};

export interface TemplateDeclaration {
    defaultValue: string;
    baseType: string;
    cardinality: string;
    identifier: string;
    isSystemVariable: boolean;
    label: string;
}

export interface FetchAudioScriptDependencies {
    type: TypeKeys.FETCH_AUDIO_SCRIPT_DEPENDENCIES;
    contentItemId: number;
}

export type SaveAudioScriptResource = {
    type: TypeKeys.SAVE_AUDIO_SCRIPT_RESOURCE;
    textContent: string;
    resourceContentItemId: number;
    featureId: string;
}

export type AddOrUpdateUrlResource = {
    type: TypeKeys.ADD_OR_UPDATE_URL_RESOURCE;
    featureId: string;
    link: string;
    title: string;
    rootUrl?: string;
    resourceContentItemId?: number;
}

export type SaveDynamicAnswerKey = {
    type: TypeKeys.SAVE_DYNAMIC_ANSWER_KEY;
    dynamicAnswerKey: Record<string, any>;
}

export type SetContentStylePresets = {
    type: TypeKeys.SET_CONTENT_STYLE_PRESETS;
    payload: ContentStylePreset[];
}

export type SetSymbolPalettePresets = {
    type: TypeKeys.SET_SYMBOL_PALETTE__PRESETS;
    payload: SymbolPreset[];
}

export type ResetAudioScriptData = {
    featureId: string;
    type: TypeKeys.RESET_AUDIO_SCRIPT_DATA;
}

export interface SetPreviousSelectedDAKData {
    featureId: string;
    type: TypeKeys.SET_PREVIOUS_SELECTED_DAK_CONFIG;
    previousSelectedDAKData: PreviousSelectedDAKData;
}

export const previewTabChange = (featureId: string, activePreviewTabIndex: number): PreviewTabChangeAction => ({
    featureId,
    activePreviewTabIndex,
    type: TypeKeys.PREVIEW_TAB_CHANGE,
});


export const storeContentEditorIframeReference = (featureId: string, iframeTarget: HTMLIFrameElement): StoreContentEditorIframeReference => ({
    featureId,
    iframeTarget,
    type: TypeKeys.STORE_EDITOR_IFRAME_REFERENCE,
});

export const requestEditorContentSave = (featureId: string, isPrintView?: boolean): ItemSaveRequest => ({
    featureId,
    isPrintView,
    type: TypeKeys.ITEM_SAVE_REQUEST,
});

export const requestItemSaveAsTemplate = (featureId: string): ItemSaveRequest => ({
    featureId,
    isTemplate: true,
    type: TypeKeys.ITEM_SAVE_REQUEST,
});

export const requestSaveDynamicAnsweKey = (featureId: string): ItemSaveRequest => ({
    featureId,
    isDynamicAnswerKey: true,
    type: TypeKeys.ITEM_SAVE_REQUEST,
});

export const requestQuestionReload = (featureId: string): RequestQuestionReload => ({
    featureId,
    type: TypeKeys.REQUEST_QUESTION_RELOAD,
});

export const editorContentSaveInProgress = (featureId: string, inProgress: boolean): ItemSaveInProgress => ({
    featureId,
    inProgress,
    type: TypeKeys.ITEM_SAVE_IN_PROGRESS,
});

export const setTemplateDeclarations = (featureId: string, data: Array<TemplateDeclaration>): SetTemplateDeclarations => ({
    featureId,
    data,
    type: TypeKeys.SET_TEMPLATE_DECLARATIONS,
});

export const setAudioScriptText = (featureId: string, data: string): SetAudioScriptText => ({
    featureId,
    data,
    type: TypeKeys.SET_AUDIO_SCRIPT_TEXT,
});

export const setAudioScriptResource = (featureId: string, data: AdditionalResource): SetAudioScriptResource => ({
    featureId,
    data,
    type: TypeKeys.SET_AUDIO_SCRIPT_RESOURCE,
});

export const setAssessmentItemXML = (featureId: string, data: any): SetAssessmentItemXML => ({
    featureId,
    data,
    type: TypeKeys.SET_ASSESSMENT_ITEM_XML,
});

export const editorContentSaveResponse = (featureId: string, contentItemId: number, payload: any): ItemSaveResponse => ({
    featureId,
    contentItemId,
    payload,
    type: TypeKeys.ITEM_SAVE_RESPONSE,
});

export const editorHasLoaded = (featureId: string): EditorLoaded => ({
    featureId,
    type: TypeKeys.EDITOR_HAS_LOADED,
});

export const resetEditorLoaded = (featureId: string): ResetEditorLoaded => ({
    featureId,
    type: TypeKeys.RESET_EDITOR_LOADED,
});

export const previewNewWindow = (featureId: string, previewLayoutMode: string): PreviewInNewWindowAction => ({
    featureId,
    previewLayoutMode,
    type: TypeKeys.PREVIEW_IN_NEW_WINDOW,
});

export const setContentEditStatus = (featureId: string, contentEditStatus: ContentEditStatus): SetContentEditStatusAction => ({
    featureId,
    contentEditStatus,
    type: TypeKeys.SET_CONTENT_EDIT_STATUS,
});

export const resetContentEditStatus = (featureId: string): ResetContentEditStatusAction => ({
    featureId,
    type: TypeKeys.RESET_CONTENT_EDIT_STATUS,
});

export const updateContentInContextToLiveRevision = (
    featureId: string,
    contentItemId: number,
    onSuccessCallback?: () => void
): UpdateContentInContextToLiveRevisionAction => ({
    featureId,
    contentItemId,
    type: TypeKeys.UPDATE_CONTENT_IN_CONTEXT_TO_LIVE_REVISION,
    onSuccessCallback,
});

export const updateTemplateAndContentInContext = (featureId: string,
    contentItemId: number, checkedOptions: UpdateCheckStatus, templateContentItemId: number):
    UpdateTemplateAndContentInContextAction => ({
    featureId,
    contentItemId,
    checkedOptions,
    templateContentItemId,
    type: TypeKeys.UPDATE_TEMPLATE_AND_CONTENT_IN_CONTEXT,
});

export const duplicateAndReplaceContentInContext = (featureId: string, contentItemId: number): DuplicateAndReplaceContentInContext => ({
    featureId,
    contentItemId,
    type: TypeKeys.DUPLICATE_AND_REPLACE_CONTENT_IN_CONTEXT,
});

export const setLoadTemplateRevisionAsItIs = (featureId: string, contentItemId: number, loadTemplateRevisionAsItIs: TemplateRevisionWarningStatuses):
 SetLoadTemplateRevisionAsItIs => ({
    featureId,
    contentItemId,
    loadTemplateRevisionAsItIs,
    type: TypeKeys.KEEP_CURRENT_CHANGES_FOR_TEMPLATE,
});

export const fetchAudioScriptDependencies = (
    contentItemId: number
): FetchAudioScriptDependencies => ({
    type: TypeKeys.FETCH_AUDIO_SCRIPT_DEPENDENCIES,
    contentItemId,
});

export const saveAudioScriptResource = (
    featureId: string,
    textContent: string,
    resourceContentItemId = -1
): SaveAudioScriptResource => ({
    featureId,
    textContent,
    resourceContentItemId,
    type: TypeKeys.SAVE_AUDIO_SCRIPT_RESOURCE,
});

export const addOrUpdateUrlResource = (
    featureId: string,
    link: string,
    title: string,
    rootUrl?: string,
    resourceContentItemId = -1
): AddOrUpdateUrlResource => ({
    featureId,
    link,
    title,
    rootUrl,
    resourceContentItemId,
    type: TypeKeys.ADD_OR_UPDATE_URL_RESOURCE,
});

export const dynamicAnswerKeyData = (
    payload: Record<string, any>,
): SaveDynamicAnswerKey => ({
    type: TypeKeys.SAVE_DYNAMIC_ANSWER_KEY,
    dynamicAnswerKey: payload,
});

export const setContentStylePresets = (
    payload: ContentStylePreset[],
): SetContentStylePresets => ({
    type: TypeKeys.SET_CONTENT_STYLE_PRESETS,
    payload,
});

export const setSymbolPalettePresets = (
    payload: SymbolPreset[],
): SetSymbolPalettePresets => ({
    type: TypeKeys.SET_SYMBOL_PALETTE__PRESETS,
    payload,
});

export const resetAudioScriptData = (featureId: string): ResetAudioScriptData => ({
    type: TypeKeys.RESET_AUDIO_SCRIPT_DATA,
    featureId,
});


export type OpenQuestionTestCasesAction = {
    featureId: string;
    type: TypeKeys.OPEN_QUESTION_TEST_CASES;
}

export const openQuestionTestCases = (featureId: string): OpenQuestionTestCasesAction => ({
    featureId,
    type: TypeKeys.OPEN_QUESTION_TEST_CASES,
});

export const setPreviousSelectedDAKData = (featureId: string, previousSelectedDAKData: PreviousSelectedDAKData): SetPreviousSelectedDAKData => ({
    featureId,
    previousSelectedDAKData,
    type: TypeKeys.SET_PREVIOUS_SELECTED_DAK_CONFIG,
});

export type SetHasXMLError = {
    featureId: string;
    type: TypeKeys.HAS_XML_ERROR;
    isErrorInXML: boolean;
}

export const setHasXMLError = (featureId: string, isErrorInXML: boolean): SetHasXMLError => ({
    featureId,
    type: TypeKeys.HAS_XML_ERROR,
    isErrorInXML,
});

const pciStimulusTemplatesActionCreators = createPciStimulusTemplatesActionCreators({
    fetch: TypeKeys.FETCH_PCI_STIMULUS_TEMPLATES,
    success: TypeKeys.FETCH_PCI_STIMULUS_TEMPLATES_SUCCESS,
    error: TypeKeys.FETCH_PCI_STIMULUS_TEMPLATES_ERROR,
});

export const {
    fetchPciStimulusTemplates,
    fetchPciStimulusTemplatesSuccess,
    fetchPciStimulusTemplatesError,
} = pciStimulusTemplatesActionCreators;

export type FetchPciStimulusTemplates = FetchPciStimulusTemplatesAction<TypeKeys.FETCH_PCI_STIMULUS_TEMPLATES>;
export type FetchPciStimulusTemplatesSuccess = FetchPciStimulusTemplatesSuccessAction<TypeKeys.FETCH_PCI_STIMULUS_TEMPLATES_SUCCESS>;
export type FetchPciStimulusTemplatesError = FetchPciStimulusTemplatesErrorAction<TypeKeys.FETCH_PCI_STIMULUS_TEMPLATES_ERROR>;

export type ActionTypes = ItemSaveRequest|StoreContentEditorIframeReference
    |RequestQuestionReload |EditorLoaded |ResetEditorLoaded |ItemSaveInProgress
    |ItemSaveResponse |PreviewInNewWindowAction
    |SetTemplateDeclarations|SetAssessmentItemXML|PreviewTabChangeAction
    |SetContentEditStatusAction|ResetContentEditStatusAction
    |UpdateContentInContextToLiveRevisionAction|DuplicateAndReplaceContentInContext
    |SetAudioScriptText | SetAudioScriptResource | FetchAudioScriptDependencies | SaveAudioScriptResource | SaveDynamicAnswerKey
    |SetContentStylePresets | SetSymbolPalettePresets | OpenQuestionTestCasesAction | ResetAudioScriptData | SetPreviousSelectedDAKData
    |SetLoadTemplateRevisionAsItIs | UpdateTemplateAndContentInContextAction | SetHasXMLError | RenameContent
    |FetchPciStimulusTemplates | FetchPciStimulusTemplatesSuccess | FetchPciStimulusTemplatesError;
